/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { DataGrid } from '../../../components/common/datagrid/ResponsiveDataGrid';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FactoryIcon from '@mui/icons-material/Factory';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import ProductionOutDialog from './ProductionOutDialog';
import * as manufacturingApi from '../../../lib/api/manufacturing';
import * as fileApi from '../../../lib/api/file';
import { syncCompletionProcess } from '../../../utils/manufacturing/routingSync';

/**
 * 출하 상세 다이얼로그 (파일 업로드 포함)
 */
const ShipmentDetailDialog = ({ shipment, planId, onClose, onComplete }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const shipmentId = shipment?.shipmentId || shipment?.id;

  // 파일 목록 로드
  useEffect(() => {
    if (!shipmentId) return;
    const loadFiles = async () => {
      try {
        const res = await fileApi.getShipmentFiles(shipmentId);
        setFiles(res.data?.list || res.data || []);
      } catch {
        setFiles([]);
      }
    };
    loadFiles();
  }, [shipmentId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await fileApi.uploadShipmentFile(file, planId, shipmentId);
      const res = await fileApi.getShipmentFiles(shipmentId);
      setFiles(res.data?.list || res.data || []);
    } catch {
      alert('파일 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = (file) => {
    const url = `/api/v1/file/down?fileUuid=${file.fileUuid}`;
    window.open(url, '_blank');
  };

  const handleDeleteFile = async (file) => {
    if (!window.confirm(`"${file.fileNm}" 파일을 삭제하시겠습니까?`)) return;
    try {
      await fileApi.removeFile(file.fileUuid);
      setFiles(prev => prev.filter(f => f.fileUuid !== file.fileUuid));
    } catch {
      alert('파일 삭제에 실패했습니다.');
    }
  };

  if (!shipment) return null;

  const isPending = shipment.status !== 'COMPLETED';

  return (
    <Dialog open={!!shipment} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>출하 상세</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">출하번호</Typography>
            <Typography variant="body2" fontWeight="bold">{shipment.shipmentNumber}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">출하일</Typography>
            <Typography variant="body2">{shipment.shipmentDate}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">출고유형</Typography>
            <Chip
              icon={shipment.sourceType === 'WAREHOUSE' ? <WarehouseIcon /> : <FactoryIcon />}
              label={shipment.sourceType === 'WAREHOUSE' ? '창고' : '납품'}
              size="small"
              variant="outlined"
              color={shipment.sourceType === 'WAREHOUSE' ? 'secondary' : 'primary'}
            />
          </Box>
          {shipment.sourceType === 'WAREHOUSE' && (
            <>
              {shipment.whNm && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">창고</Typography>
                  <Typography variant="body2">{shipment.whNm}</Typography>
                </Box>
              )}
              {shipment.stockProductName && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">재고 제품</Typography>
                  <Typography variant="body2">{shipment.stockProductName}</Typography>
                </Box>
              )}
            </>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">수량</Typography>
            <Typography variant="body2" fontWeight="bold">{shipment.quantity?.toLocaleString()}개</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">상태</Typography>
            <Chip
              label={shipment.status === 'COMPLETED' ? '완료' : '대기'}
              size="small"
              color={shipment.status === 'COMPLETED' ? 'success' : 'default'}
            />
          </Box>
          {shipment.remarks && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">비고</Typography>
              <Typography variant="body2">{shipment.remarks}</Typography>
            </Box>
          )}

          <Divider sx={{ my: 1 }} />

          {/* 첨부파일 */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2">첨부파일</Typography>
            <Button
              size="small"
              startIcon={uploading ? <CircularProgress size={14} /> : <AttachFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              파일 추가
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              onChange={handleUpload}
            />
          </Box>

          {files.length > 0 ? (
            <List dense sx={{ bgcolor: '#f9f9f9', borderRadius: 1 }}>
              {files.map((file, idx) => (
                <ListItem
                  key={file.fileUuid || idx}
                  secondaryAction={
                    <Box>
                      <IconButton size="small" onClick={() => handleDownload(file)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteFile(file)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <InsertDriveFileIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={file.fileNm}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: 'center' }}>
              첨부된 파일이 없습니다.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
        {isPending && (
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={onComplete}
          >
            출하 완료
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

/**
 * 완료/출하 탭 컨테이너
 */
const CompletionTabContainer = ({ planId, planData, onRoutingUpdate }) => {
  // 상태 관리
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMockData, setIsMockData] = useState(false);
  const [shipmentSaveError, setShipmentSaveError] = useState(null);
  // 창고 입고. 고객에게 바로 안 가고 창고에서 대기하는 경우다.
  const [openWarehouseIn, setOpenWarehouseIn] = useState(false);
  const [savingWarehouseIn, setSavingWarehouseIn] = useState(false);
  const [warehouseInError, setWarehouseInError] = useState(null);
  const [submittingShipment, setSubmittingShipment] = useState(false);
  
  // 요약 데이터
  const [summary, setSummary] = useState(null);
  
  // 출하 내역
  const [shipments, setShipments] = useState([]);
  
  // 생산 공정 완료 여부
  const [qcCompleted, setQcCompleted] = useState(false);
  const [qcCompletedCount, setQcCompletedCount] = useState(0);  
  
  // 다이얼로그 상태
  const [openShipmentDialog, setOpenShipmentDialog] = useState(false);
  const [openCompleteProductionDialog, setOpenCompleteProductionDialog] = useState(false);
  const [openCompleteShipmentDialog, setOpenCompleteShipmentDialog] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);

  // ===========================================
  // 데이터 로드
  // ===========================================
  const loadMockData = useCallback(() => {
    console.log('완료/출하 - 데이터 없음');
    setSummary(null);
    setShipments([]);
    setQcCompleted(false);
    setQcCompletedCount(0);
    setIsMockData(false);
  }, []);

  const fetchData = useCallback(async () => {
    if (!planId) {
      loadMockData();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 라우팅 공정 완료 여부 확인 (QC 포함 모든 공정)
      let qcCount = 0;
      let qcCompletedFlag = false;
      try {
        const routingRes = await manufacturingApi.getRoutingsByPlan(planId);
        if (routingRes.data.success && routingRes.data.data && routingRes.data.data.length > 0) {
          const routingData = routingRes.data.data[0];
          const details = routingData.details || [];
          const total = details.length;
          const completed = details.filter(d => d.processStatus === 'COMPLETED' || d.processStatus === 'SKIPPED').length;
          qcCount = completed;
          qcCompletedFlag = total > 0 && completed === total;
        }
      } catch (routingErr) {
        console.log('라우팅 조회 실패:', routingErr);
      }
      setQcCompleted(qcCompletedFlag);
      setQcCompletedCount(qcCount);
      
      // 완료 요약 조회
      try {
        const summaryRes = await manufacturingApi.getCompletionSummary(planId);
        if (summaryRes.data.success) {
          setSummary({
            ...summaryRes.data.data,
            qcCompleted: qcCompletedFlag,
            qcCompletedCount: qcCount,
          });
        }
      } catch (summaryErr) {
        console.log('요약 조회 실패, planData에서 구성:', summaryErr);
        // planData에서 기본 요약 구성 (planQty 또는 targetQuantity 사용)
        const planQty = planData?.planQty || planData?.targetQuantity || 0;
        setSummary({
          planId: planId,
          planNumber: planData?.planNumber || '-',
          productName: planData?.productName || '-',
          planQuantity: planQty,
          producedQuantity: 0,
          qcPassQuantity: qcCount,
          qcFailQuantity: 0,
          shippableQuantity: qcCount > 0 ? planQty : 0,
          shippedQuantity: 0,
          warehousedQuantity: 0,
          inWarehouseQuantity: 0,
          remainingQuantity: qcCount > 0 ? planQty : 0,
          productionProgress: 0,
          shipmentProgress: 0,
          planStatus: planData?.status || 'IN_PROGRESS',
          isProductionCompleted: false,
          isShipmentCompleted: false,
          qcCompleted: qcCompletedFlag,
          qcCompletedCount: qcCount,
        });
      }
      
      // 출하 목록 조회
      try {
        const shipmentsRes = await manufacturingApi.getShipmentsByPlan(planId);
        if (shipmentsRes.data.success) {
          const list = shipmentsRes.data.data || [];
          setShipments(list.map(item => ({
            ...item,
            id: item.shipmentId,
          })));
        }
      } catch (shipErr) {
        console.log('출하 목록 조회 실패:', shipErr);
        setShipments([]);
      }
      
      setIsMockData(false);
    } catch (err) {
      console.error('완료/출하 데이터 조회 에러:', err);
      setError('데이터를 불러오는데 실패했습니다.');
      setSummary(null);
      setShipments([]);
    } finally {
      setLoading(false);
    }
  }, [planId, planData, loadMockData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ===========================================
  // 출하 컬럼 정의
  // ===========================================
  const columns = [
    {
      field: 'shipmentNumber',
      headerName: '출하번호',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontWeight="bold"
          color="primary"
          sx={{ cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: '#1565c0' } }}
          onClick={(e) => { e.stopPropagation(); setSelectedShipment(params.row); }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'sourceType',
      headerName: '출고유형',
      width: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          icon={params.value === 'WAREHOUSE' ? <WarehouseIcon /> : <FactoryIcon />}
          label={params.value === 'WAREHOUSE' ? '창고' : '납품'}
          size="small"
          variant="outlined"
          color={params.value === 'WAREHOUSE' ? 'secondary' : 'primary'}
        />
      ),
    },
    {
      field: 'shipmentDate',
      headerName: '출하일',
      width: 120,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'quantity',
      headerName: '수량',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.value?.toLocaleString()}개
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: '상태',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={params.row.statusName || (params.value === 'COMPLETED' ? '완료' : '대기')}
          size="small"
          color={params.value === 'COMPLETED' ? 'success' : 'default'}
          variant="filled"
        />
      ),
    },
    {
      field: 'remarks',
      headerName: '비고',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'createdAt',
      headerName: '등록일',
      width: 120,
      align: 'center',
      headerAlign: 'center',
    },
  ];

  // ===========================================
  // 이벤트 핸들러
  // ===========================================

  const pendingShipments = shipments.filter((shipment) => shipment.status !== 'COMPLETED');
  const hasPendingShipment = pendingShipments.length > 0;

  const handleCloseShipmentDialog = useCallback(() => {
    if (submittingShipment) {
      return;
    }
    setShipmentSaveError(null);
    setOpenShipmentDialog(false);
  }, [submittingShipment]);
  
  /**
   * 창고 입고.
   *
   * 성공하면 요약과 출하 내역을 다시 읽는다. 창고로 간 것도 출하 내역에 한 줄 남는다 —
   * 생산 입장에서는 물건이 나간 것이라, 목록에 안 보이면 줄어든 수량과 화면이 어긋난다.
   */
  const handleWarehouseIn = useCallback(async (payload) => {
    if (!planId) {
      setWarehouseInError('생산계획 정보가 없습니다.');
      return;
    }
    setSavingWarehouseIn(true);
    setWarehouseInError(null);
    try {
      const res = await manufacturingApi.warehouseInFromProduction(planId, payload);
      if (res.data?.success) {
        setOpenWarehouseIn(false);
        await fetchData();
      } else {
        setWarehouseInError(res.data?.message || '창고 입고에 실패했습니다.');
      }
    } catch (err) {
      setWarehouseInError(err?.response?.data?.message || '창고 입고에 실패했습니다.');
    } finally {
      setSavingWarehouseIn(false);
    }
  }, [planId, fetchData]);

  /**
   * 즉시 출고.
   *
   * 창고에 넣고 바로 내보내는 것을 서버가 한 번에 한다. 사용자는 한 번 누르고,
   * 이력에는 입고와 출고가 모두 남는다. 창고를 거치지 않는 예외 경로를 두지 않으려는 것이다.
   *
   * 창고 재고를 골라 내보내는 일은 여기 없다. 그건 출고관리에서 프로젝트를 지정해 한다 —
   * 재고가 자기 주인을 들고 있어서 특정 생산계획 화면 안에서 할 이유가 없다.
   */
  const handleShipNow = useCallback(async (payload) => {
    if (submittingShipment) return;
    if (!planId) {
      setShipmentSaveError('생산계획 정보가 없습니다.');
      return;
    }
    if (hasPendingShipment) {
      setShipmentSaveError('대기 상태 출하가 있어 추가 등록할 수 없습니다. 기존 출하를 완료한 뒤 다시 등록해주세요.');
      return;
    }

    setShipmentSaveError(null);
    setSubmittingShipment(true);
    try {
      const res = await manufacturingApi.shipNowFromProduction(planId, payload);
      if (res.data?.success) {
        setOpenShipmentDialog(false);
        await fetchData();
      } else {
        setShipmentSaveError(res.data?.message || '출고에 실패했습니다.');
      }
    } catch (err) {
      setShipmentSaveError(err?.response?.data?.message || '출고에 실패했습니다.');
    } finally {
      setSubmittingShipment(false);
    }
  }, [planId, fetchData, hasPendingShipment, submittingShipment]);

  // 생산완료 처리
  const handleCompleteProduction = useCallback(async () => {
    try {
      const response = await manufacturingApi.completeProduction(planId);

      if (response.data.success) {
        fetchData();
        // 라우팅 "생산완료" 공정 → COMPLETED
        await syncCompletionProcess(planId, 'COMPLETED', onRoutingUpdate);
      } else {
        alert(response.data.message || '생산완료 처리에 실패했습니다.');
      }
    } catch (err) {
      console.error('생산완료 처리 에러:', err);
      alert('생산완료 처리에 실패했습니다.');
    }

    setOpenCompleteProductionDialog(false);
  }, [planId, fetchData, onRoutingUpdate]);

  // 출하완료 처리
  const handleCompleteShipment = useCallback(async () => {
    try {
      const response = await manufacturingApi.completeShipment(planId);
      
      if (response.data.success) {
        fetchData();
      }
    } catch (err) {
      console.error('출하완료 처리 에러:', err);
      
      if (isMockData) {
        setSummary(prev => ({
          ...prev,
          isShipmentCompleted: true,
          planStatus: 'SHIPPED',
          planStatusName: '출하완료',
        }));
      }
    }
    
    setOpenCompleteShipmentDialog(false);
  }, [planId, isMockData, fetchData]);

  // ===========================================
  // 렌더링
  // ===========================================
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  const canCompleteProduction = summary && !summary.isProductionCompleted && qcCompleted;
  const canCompleteShipment = summary && summary.isProductionCompleted && !summary.isShipmentCompleted && summary.remainingQuantity <= 0;
  // QC 완료되어야 출하 가능 (창고 출고는 잔여 수량/대기 출하 무관하게 가능)
  const canAddShipment = summary && qcCompleted;
  const shipmentStatusMeta = (() => {
    if (summary?.isShipmentCompleted) {
      return { label: '출하완료', color: 'success', variant: 'filled' };
    }
    if ((summary?.shippedQuantity || 0) > 0) {
      return { label: '출하중', color: 'primary', variant: 'filled' };
    }
    if ((summary?.shippableQuantity || 0) > 0) {
      return { label: '출하대기', color: 'info', variant: 'outlined' };
    }
    return { label: '출하 미완료', color: 'default', variant: 'outlined' };
  })();

  return (
    <Box>
      {/* 에러 알림 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 데이터 없음 알림 */}
      {!summary && !error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          생산 완료/출하 데이터가 없습니다.
        </Alert>
      )}

      {/* 요약 + 완료 처리 */}
      <Paper elevation={1} sx={{ p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ textAlign: 'center', px: 2 }}>
              <Typography variant="caption" color="text.secondary">계획 수량</Typography>
              <Typography variant="h6" fontWeight="bold">
                {summary?.planQuantity?.toLocaleString() || 0}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            {/* 고객에게 간 것과 창고에서 기다리는 것을 나눠 보여준다.
                한 칸으로 묶으면 창고에 넣은 물건을 나중에 내보낼 때 두 번 세어진다. */}
            <Box sx={{ textAlign: 'center', px: 2 }}>
              <Typography variant="caption" color="text.secondary">고객 납품</Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {summary?.shippedQuantity?.toLocaleString() || 0}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ textAlign: 'center', px: 2 }}>
              <Typography variant="caption" color="text.secondary">창고 대기</Typography>
              <Typography
                variant="h6"
                fontWeight="bold"
                color={summary?.inWarehouseQuantity > 0 ? 'warning.main' : 'text.secondary'}
              >
                {summary?.inWarehouseQuantity?.toLocaleString() || 0}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ textAlign: 'center', px: 2 }}>
              <Typography variant="caption" color="text.secondary">남은 수량</Typography>
              <Typography variant="h6" fontWeight="bold" color={summary?.remainingQuantity > 0 ? 'warning.main' : 'success.main'}>
                {summary?.remainingQuantity?.toLocaleString() || 0}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              icon={<FactoryIcon />}
              label={summary?.isProductionCompleted ? '생산완료' : '생산 미완료'}
              size="small"
              color={summary?.isProductionCompleted ? 'success' : 'default'}
              variant={summary?.isProductionCompleted ? 'filled' : 'outlined'}
            />
            <Chip
              icon={<LocalShippingIcon />}
              label={shipmentStatusMeta.label}
              size="small"
              color={shipmentStatusMeta.color}
              variant={shipmentStatusMeta.variant}
            />
            {canCompleteProduction && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<AssignmentTurnedInIcon />}
                onClick={() => setOpenCompleteProductionDialog(true)}
              >
                생산완료
              </Button>
            )}
            {canCompleteShipment && (
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<CheckCircleIcon />}
                onClick={() => setOpenCompleteShipmentDialog(true)}
              >
                출하완료
              </Button>
            )}
          </Box>
        </Box>

        {!qcCompleted && (
          <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
            모든 공정 완료 후 출하를 등록할 수 있습니다. (생산 탭에서 공정을 완료해주세요)
          </Alert>
        )}
        {qcCompleted && hasPendingShipment && (
          <Alert severity="info" variant="outlined" sx={{ mt: 1, py: 0.5 }}>
            대기 상태 출하가 {pendingShipments.length}건 있습니다. 기존 출하를 완료한 뒤 새 출하를 등록할 수 있습니다.
          </Alert>
        )}
      </Paper>

      {/* 출하 내역 */}
      <Paper elevation={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShippingIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight="bold">출하 내역</Typography>
            <Chip label={`${shipments.length}건`} size="small" color="default" />
            {hasPendingShipment && (
              <Chip label={`대기 ${pendingShipments.length}건`} size="small" color="info" variant="outlined" />
            )}
            {summary?.remainingQuantity > 0 && (
              <Chip label={`잔여 ${summary.remainingQuantity}개`} size="small" color="warning" variant="outlined" />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* 창고 입고. 고객에게 바로 안 보내고 창고에 넣어둘 때 쓴다. */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<WarehouseIcon />}
              onClick={() => {
                setWarehouseInError(null);
                setOpenWarehouseIn(true);
              }}
              disabled={!canAddShipment}
            >
              창고 입고
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setShipmentSaveError(null);
                setOpenShipmentDialog(true);
              }}
              disabled={!canAddShipment}
            >
              즉시 출고
            </Button>
          </Box>
        </Box>
        <DataGrid
          rows={shipments}
          columns={columns}
          autoHeight
          density="compact"
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 20]}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
          }}
        />
      </Paper>

      {/* 창고 입고 — 창고에 넣어두기만 한다 */}
      <ProductionOutDialog
        open={openWarehouseIn}
        mode="WAREHOUSE_IN"
        planData={planData}
        maxQuantity={summary?.remainingQuantity || 0}
        saving={savingWarehouseIn}
        saveError={warehouseInError}
        onClose={() => { if (!savingWarehouseIn) setOpenWarehouseIn(false); }}
        onSave={handleWarehouseIn}
      />

      {/* 즉시 출고 — 창고에 넣고 바로 내보낸다 */}
      <ProductionOutDialog
        open={openShipmentDialog}
        mode="SHIP_NOW"
        planData={planData}
        maxQuantity={summary?.remainingQuantity || 0}
        saving={submittingShipment}
        saveError={shipmentSaveError}
        onClose={handleCloseShipmentDialog}
        onSave={handleShipNow}
      />

      {/* 출하 상세 다이얼로그 */}
      <ShipmentDetailDialog
        shipment={selectedShipment}
        planId={planId}
        onClose={() => setSelectedShipment(null)}
        onComplete={async () => {
          try {
            const response = await manufacturingApi.updateShipmentStatus(selectedShipment.shipmentId || selectedShipment.id, {
              status: 'COMPLETED',
            });
            if (response.data?.success === false) {
              throw new Error(response.data.message || '출하 완료 처리에 실패했습니다.');
            }
            setSelectedShipment(null);
            await fetchData();
          } catch (err) {
            console.error('출하 완료 처리 에러:', err);
            alert(err?.response?.data?.message || err?.message || '출하 완료 처리에 실패했습니다.');
          }
        }}
      />

      {/* 생산완료 확인 다이얼로그 */}
      <Dialog open={openCompleteProductionDialog} onClose={() => setOpenCompleteProductionDialog(false)}>
        <DialogTitle>생산완료 처리</DialogTitle>
        <DialogContent>
          <DialogContentText>
            생산완료 처리를 진행하시겠습니까?
            <br /><br />
            <strong>공정 완료: {qcCompletedCount}건</strong>
            <br />
            생산완료 처리 후 출하를 등록할 수 있습니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCompleteProductionDialog(false)}>취소</Button>
          <Button onClick={handleCompleteProduction} variant="contained" color="primary">
            생산완료 처리
          </Button>
        </DialogActions>
      </Dialog>

      {/* 출하완료 확인 다이얼로그 */}
      <Dialog open={openCompleteShipmentDialog} onClose={() => setOpenCompleteShipmentDialog(false)}>
        <DialogTitle>출하완료 처리</DialogTitle>
        <DialogContent>
          <DialogContentText>
            출하완료 처리를 진행하시겠습니까?
            <br /><br />
            <strong>총 출하 수량: {summary?.shippedQuantity?.toLocaleString()}개</strong>
            <br />
            출하완료 처리 후에는 추가 출하가 불가능합니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCompleteShipmentDialog(false)}>취소</Button>
          <Button onClick={handleCompleteShipment} variant="contained" color="success">
            출하완료 처리
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompletionTabContainer;
